// NotesSection - free-text reviewer notes shown on the Person page.
//
// Display rules:
//   • Renders NOTHING when there are no notes AND the user isn't actively
//     composing. Keeps the page free of empty placeholders.
//   • When the parent toggles `composeOpen` (e.g., via the Actions menu's
//     "Add a note" item), shows a compact compose row at the top.
//   • When notes exist, lists them in reverse-chronological order
//     using a tight, dense layout.
//
// Notes save to the server immediately. All errors fall back to a small
// inline message so the reviewer knows nothing was lost.

import { useEffect, useRef, useState } from "react";
import { listNotes, createNote, updateNote, deleteNote, type NoteRow } from "../api";
import { toast } from "./Toaster";

interface Props {
  jobId: string;
  /** Parent-controlled compose flag. When true, the compose textarea is
   *  shown + autofocused. Toggled by the Actions menu's "Add a note"
   *  item or by the inline "+ note" button on the section header. */
  composeOpen: boolean;
  onComposeOpen: () => void;
  onComposeClose: () => void;
  /** Optional callback fired whenever the notes load or change. Lets the
   *  parent (PersonPage) gate its wrapping card on whether there's
   *  anything to show. */
  onNotesCountChange?: (count: number) => void;
}

export function NotesSection({ jobId, composeOpen, onComposeOpen, onComposeClose, onNotesCountChange }: Props): JSX.Element | null {
  const [notes, setNotes] = useState<NoteRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingDraft, setEditingDraft] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const composeRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    let alive = true;
    listNotes(jobId)
      .then((ns) => alive && setNotes(ns))
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [jobId]);

  // Notify parent when the notes count changes so a wrapping card can
  // self-hide when there are no notes and the user isn't composing.
  useEffect(() => {
    onNotesCountChange?.(notes?.length ?? 0);
  }, [notes, onNotesCountChange]);

  // Autofocus the compose textarea + scroll into view when the parent
  // opens compose (typically from the Actions menu).
  useEffect(() => {
    if (composeOpen && composeRef.current) {
      composeRef.current.focus();
      composeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [composeOpen]);

  const onAdd = async () => {
    const body = draft.trim();
    if (!body || saving) return;
    setSaving(true);
    setError(null);
    try {
      const note = await createNote(jobId, body);
      setNotes((prev) => [note, ...(prev ?? [])]);
      setDraft("");
      onComposeClose();
      toast("Note added", "success");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast(`Couldn't add note - ${msg}`, "danger");
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    setDraft("");
    onComposeClose();
  };

  const onStartEdit = (n: NoteRow) => {
    setEditingId(n.id);
    setEditingDraft(n.body);
  };
  const onCancelEdit = () => {
    setEditingId(null);
    setEditingDraft("");
  };
  const onSaveEdit = async (id: number) => {
    const body = editingDraft.trim();
    if (!body) return;
    setError(null);
    try {
      const updated = await updateNote(jobId, id, body);
      setNotes((prev) => prev?.map((n) => (n.id === id ? updated : n)) ?? null);
      setEditingId(null);
      setEditingDraft("");
      toast("Note updated", "success");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast(`Couldn't save edit - ${msg}`, "danger");
    }
  };
  const onDelete = async (id: number) => {
    setConfirmDeleteId(null);
    setDeletingId(id);
    setError(null);
    try {
      await deleteNote(jobId, id);
      setNotes((prev) => prev?.filter((n) => n.id !== id) ?? null);
      toast("Note deleted", "warn");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast(`Couldn't delete note - ${msg}`, "danger");
    } finally {
      setDeletingId(null);
    }
  };

  // Hide the section entirely when there's nothing to show + the user
  // isn't composing. Avoids the previous always-on placeholder that
  // ate vertical space on every Person page.
  const hasNotes = (notes?.length ?? 0) > 0;
  if (!hasNotes && !composeOpen) return null;

  return (
    <section
      className="container"
      style={{
        // Tighter than the previous "station-tight" - Notes shouldn't
        // get the editorial-section breathing room since it's reviewer
        // metadata, not a primary part of the page narrative.
        paddingTop: 14,
        paddingBottom: 14,
      }}
    >
      {/* Inner "Notes" heading removed - the parent page-card supplies
          a giant Fraunces "Notes" header. We only render the tiny count
          + "Add a note" button row here. */}
      <div className="flex items-center justify-between gap-3 flex-wrap" style={{ marginBottom: 8 }}>
        <div className="flex items-baseline gap-3">
          {hasNotes && (
            <span
              style={{
                color: "var(--color-text-dim)",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                letterSpacing: "0.06em",
              }}
            >
              {notes!.length} {notes!.length === 1 ? "note" : "notes"}
            </span>
          )}
        </div>
        {hasNotes && !composeOpen && (
          <button
            type="button"
            onClick={onComposeOpen}
            className="rounded px-2.5 py-1 text-[12px] font-semibold transition hover:bg-white/5"
            style={{
              color: "var(--color-text-muted)",
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.04em",
              border: "1px solid var(--color-line-hi)",
            }}
          >
            + Add a note
          </button>
        )}
      </div>

      {/* Compose row - only when actively composing. Inline, no big card
          container. Quiet left border that uses the orange accent so
          it reads as "active draft space". */}
      {composeOpen && (
        <div
          style={{
            padding: "10px 12px",
            background: "color-mix(in oklab, var(--color-orange-hi) 8%, transparent)",
            border: "1px solid color-mix(in oklab, var(--color-orange-hi) 35%, var(--color-line))",
            borderRadius: 8,
            marginBottom: hasNotes ? 12 : 0,
          }}
        >
          <textarea
            ref={composeRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Jot something: observations, clarifications, things to remember…"
            rows={3}
            style={{
              width: "100%",
              background: "color-mix(in oklab, var(--color-text) 3%, transparent)",
              color: "var(--color-text)",
              border: "1px solid var(--color-line-hi)",
              borderRadius: 6,
              padding: "10px 12px",
              fontFamily: "var(--font-serif)",
              fontSize: 15,
              lineHeight: 1.5,
              resize: "vertical",
              outline: "none",
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(232,160,80,0.5)")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-line-hi)")}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                onCancel();
              } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                void onAdd();
              }
            }}
          />
          <div className="mt-2 flex items-center justify-between">
            <span
              style={{
                color: "var(--color-text-dim)",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                letterSpacing: "0.03em",
              }}
            >
              ⌘+Enter to save · Esc to cancel
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="rounded px-3 py-1 text-[12px] font-semibold transition hover:bg-white/5"
                style={{
                  color: "var(--color-text-muted)",
                  fontFamily: "var(--font-mono)",
                  letterSpacing: "0.04em",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onAdd}
                disabled={!draft.trim() || saving}
                className="rounded px-3 py-1 text-[12px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  background: "var(--color-orange-hi)",
                  color: "white",
                  fontFamily: "var(--font-mono)",
                  letterSpacing: "0.06em",
                }}
              >
                {saving ? "Saving…" : "Save note"}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div
          className="rounded border px-3 py-2 text-[13px]"
          style={{
            borderColor: "var(--color-rose-hi)",
            color: "var(--color-rose-hi)",
            background: "rgba(232,99,107,0.08)",
            marginBottom: 8,
          }}
        >
          {error}
        </div>
      )}

      {/* Dense list - left accent stripe, no card-around-each-note. */}
      {hasNotes && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {notes!.map((n, i) => (
            <NoteRow
              key={n.id}
              note={n}
              isFirst={i === 0}
              isLast={i === notes!.length - 1}
              isEditing={editingId === n.id}
              editingDraft={editingDraft}
              setEditingDraft={setEditingDraft}
              onStartEdit={() => onStartEdit(n)}
              onCancelEdit={onCancelEdit}
              onSaveEdit={() => onSaveEdit(n.id)}
              onDelete={() => onDelete(n.id)}
              deleting={deletingId === n.id}
              confirmingDelete={confirmDeleteId === n.id}
              onRequestDelete={() => setConfirmDeleteId(n.id)}
              onCancelDelete={() => setConfirmDeleteId(null)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function NoteRow({
  note,
  isFirst,
  isLast,
  isEditing,
  editingDraft,
  setEditingDraft,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  deleting,
  confirmingDelete,
  onRequestDelete,
  onCancelDelete,
}: {
  note: NoteRow;
  isFirst: boolean;
  isLast: boolean;
  isEditing: boolean;
  editingDraft: string;
  setEditingDraft: (s: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
  confirmingDelete: boolean;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        padding: "10px 0",
        borderTop: isFirst ? "none" : "1px solid var(--color-line)",
        opacity: deleting ? 0.5 : 1,
        transition: "opacity 0.2s ease",
        marginBottom: isLast ? 0 : 0,
      }}
    >
      {isEditing ? (
        <>
          <textarea
            value={editingDraft}
            onChange={(e) => setEditingDraft(e.target.value)}
            rows={Math.max(3, editingDraft.split("\n").length)}
            style={{
              width: "100%",
              background: "color-mix(in oklab, var(--color-text) 3%, transparent)",
              color: "var(--color-text)",
              border: "1px solid rgba(232,160,80,0.4)",
              borderRadius: 6,
              padding: "8px 10px",
              fontFamily: "var(--font-serif)",
              fontSize: 15,
              lineHeight: 1.5,
              resize: "vertical",
              outline: "none",
            }}
            autoFocus
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancelEdit}
              className="rounded px-2.5 py-1 text-[12px] font-semibold transition hover:bg-white/5"
              style={{ color: "var(--color-text-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.04em" }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSaveEdit}
              disabled={!editingDraft.trim()}
              className="rounded px-2.5 py-1 text-[12px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
              style={{ background: "var(--color-orange-hi)", color: "white", fontFamily: "var(--font-mono)", letterSpacing: "0.04em" }}
            >
              Save
            </button>
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              fontFamily: "var(--font-serif)",
              fontSize: 15,
              lineHeight: 1.55,
              color: "var(--color-text)",
              whiteSpace: "pre-wrap",
            }}
          >
            {note.body}
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-3 flex-wrap">
            <div
              style={{ color: "var(--color-text-dim)", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.04em" }}
            >
              {formatStamp(note.created_at)}
              {note.updated_at && (
                <span style={{ marginLeft: 8, opacity: 0.85 }}>· edited {formatStamp(note.updated_at)}</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={onStartEdit}
                className="rounded px-2 py-0.5 text-[11px] font-semibold transition hover:bg-white/5"
                style={{ color: "var(--color-text-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.06em" }}
              >
                Edit
              </button>
              {confirmingDelete ? (
                <span className="flex items-center gap-1">
                  <span
                    style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--color-text-muted)", letterSpacing: "0.04em" }}
                  >
                    Delete?
                  </span>
                  <button
                    type="button"
                    onClick={onDelete}
                    disabled={deleting}
                    className="rounded px-2 py-0.5 text-[11px] font-semibold transition disabled:opacity-50"
                    style={{ color: "white", background: "var(--color-rose-hi)", fontFamily: "var(--font-mono)", letterSpacing: "0.06em" }}
                  >
                    {deleting ? "…" : "Yes"}
                  </button>
                  <button
                    type="button"
                    onClick={onCancelDelete}
                    disabled={deleting}
                    className="rounded px-2 py-0.5 text-[11px] font-semibold transition hover:bg-white/5 disabled:opacity-50"
                    style={{ color: "var(--color-text-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.06em" }}
                  >
                    No
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={onRequestDelete}
                  disabled={deleting}
                  className="rounded px-2 py-0.5 text-[11px] font-semibold transition hover:bg-white/5 disabled:opacity-50"
                  style={{ color: "var(--color-rose-hi)", fontFamily: "var(--font-mono)", letterSpacing: "0.06em" }}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date} at ${time}`;
}
